import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Truck,
  Plus,
  Search,
  Filter,
  Package,
  MapPin,
  Calendar,
  Eye,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface Shipment {
  id: string;
  shipment_number: string;
  customer_id: string | null;
  customer_name: string | null;
  shipping_date: string | null;
  delivery_date: string | null;
  carrier: string | null;
  tracking_number: string | null;
  destination_address: string | null;
  status: string;
  total_quantity: number;
  notes: string | null;
  created_at: string;
}

export function ShipmentsView() {
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);

  const { data: shipments, isLoading } = useQuery({
    queryKey: ["shipments"],
    queryFn: async () => {
      const { data, error } = await api
        .from("shipments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Shipment[];
    },
  });

  const filteredShipments = shipments?.filter((shipment) => {
    const matchesSearch =
      searchTerm === "" ||
      shipment.shipment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || shipment.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "warehouse";

  const stats = {
    total: shipments?.length || 0,
    pending: shipments?.filter((s) => s.status === "pending").length || 0,
    shipped: shipments?.filter((s) => s.status === "shipped" || s.status === "in_transit").length || 0,
    delivered: shipments?.filter((s) => s.status === "delivered").length || 0,
  };

  const handleCancelShipment = async (shipment: any) => {
    if (!window.confirm(`¿Eliminar el envío "${shipment.shipment_number}"? Se marcará como cancelado y se conserva para trazabilidad.`)) return;
    const { error } = await api.from("shipments").update({ status: "cancelled", updated_at: new Date().toISOString() }).eq("id", shipment.id);
    if (error) { toast.error("No se pudo eliminar: " + error.message); return; }
    toast.success("Envío eliminado (cancelado, se conserva el histórico)");
    queryClient.invalidateQueries({ queryKey: ["shipments"] });
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; color: string; icon: any }> = {
      pending: { label: "Pendiente", color: "bg-gray-500/10 text-gray-500", icon: Clock },
      shipped: { label: "Enviado", color: "bg-blue-500/10 text-blue-500", icon: Truck },
      in_transit: { label: "En Transito", color: "bg-yellow-500/10 text-yellow-500", icon: Truck },
      delivered: { label: "Entregado", color: "bg-green-500/10 text-green-500", icon: CheckCircle2 },
      returned: { label: "Devuelto", color: "bg-red-500/10 text-red-500", icon: AlertTriangle },
    };
    const { label, color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={cn("gap-1", color)}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Truck className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Envios</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-500/10">
                <Clock className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Truck className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.shipped}</p>
                <p className="text-sm text-muted-foreground">En Camino</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
                <p className="text-sm text-muted-foreground">Entregados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por numero, cliente o tracking..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="shipped">Enviados</SelectItem>
            <SelectItem value="in_transit">En Transito</SelectItem>
            <SelectItem value="delivered">Entregados</SelectItem>
            <SelectItem value="returned">Devueltos</SelectItem>
          </SelectContent>
        </Select>
        {canManage && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Envio
          </Button>
        )}
      </div>

      {/* Shipments List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        </div>
      ) : filteredShipments && filteredShipments.length > 0 ? (
        <div className="space-y-3">
          {filteredShipments.map((shipment) => (
            <Card
              key={shipment.id}
              className={cn(
                "hover:shadow-lg transition-all",
                shipment.status === "in_transit" && "border-l-4 border-l-yellow-500",
                shipment.status === "delivered" && "border-l-4 border-l-green-500"
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-lg">{shipment.shipment_number}</h3>
                        {getStatusBadge(shipment.status)}
                        {shipment.status !== "cancelled" && <Button variant="ghost" size="sm" className="h-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleCancelShipment(shipment); }}>Eliminar</Button>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {shipment.customer_name || "Sin cliente"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right hidden md:block">
                      <p className="text-sm text-muted-foreground">Cantidad</p>
                      <p className="font-bold">{shipment.total_quantity} uds</p>
                    </div>
                    {shipment.carrier && (
                      <div className="text-right hidden lg:block">
                        <p className="text-sm text-muted-foreground">Transportista</p>
                        <p className="font-medium">{shipment.carrier}</p>
                      </div>
                    )}
                    {shipment.shipping_date && (
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Fecha Envio</p>
                        <p className="font-medium">
                          {format(new Date(shipment.shipping_date), "dd/MM/yyyy", { locale: es })}
                        </p>
                      </div>
                    )}
                    <Button variant="ghost" size="icon">
                      <Eye className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {(shipment.tracking_number || shipment.destination_address) && (
                  <div className="mt-3 pt-3 border-t flex items-center gap-4 text-sm text-muted-foreground">
                    {shipment.tracking_number && (
                      <div className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        Tracking: {shipment.tracking_number}
                      </div>
                    )}
                    {shipment.destination_address && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {shipment.destination_address.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Truck className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || statusFilter !== "all"
                ? "No se encontraron envios con los filtros seleccionados"
                : "No hay envios registrados"}
            </p>
            {canManage && (
              <Button onClick={() => setDialogOpen(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Crear Primer Envio
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <ShipmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["shipments"] })}
      />
    </div>
  );
}

// Dialog para crear envio
interface ShipmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function ShipmentFormDialog({ open, onOpenChange, onSuccess }: ShipmentFormDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    shipment_number: "",
    customer_name: "",
    shipping_date: "",
    carrier: "",
    tracking_number: "",
    destination_address: "",
    total_quantity: "",
    notes: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.shipment_number) {
      toast.error("El numero de envio es requerido");
      return;
    }

    setLoading(true);
    try {
      const user = getCurrentUser();

      const { error } = await api.from("shipments").insert({
        shipment_number: formData.shipment_number,
        customer_name: formData.customer_name || null,
        shipping_date: formData.shipping_date || null,
        carrier: formData.carrier || null,
        tracking_number: formData.tracking_number || null,
        destination_address: formData.destination_address || null,
        total_quantity: parseInt(formData.total_quantity) || 0,
        notes: formData.notes || null,
        created_by: user?.id,
        status: "pending",
      });

      if (error) throw error;

      toast.success("Envio creado correctamente");
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al crear envio");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      shipment_number: "",
      customer_name: "",
      shipping_date: "",
      carrier: "",
      tracking_number: "",
      destination_address: "",
      total_quantity: "",
      notes: "",
    });
  };

  const generateShipmentNumber = () => {
    const date = new Date();
    const num = `ENV-${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(Math.floor(Math.random() * 1000)).padStart(3, "0")}`;
    setFormData({ ...formData, shipment_number: num });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Nuevo Envio
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Numero de Envio *</Label>
            <div className="flex gap-2">
              <Input
                value={formData.shipment_number}
                onChange={(e) => setFormData({ ...formData, shipment_number: e.target.value })}
                placeholder="ENV-YYYYMMDD-001"
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={generateShipmentNumber}>
                Generar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Nombre del cliente"
              />
            </div>
            <div className="space-y-2">
              <Label>Cantidad Total</Label>
              <Input
                type="number"
                value={formData.total_quantity}
                onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha de Envio</Label>
              <Input
                type="date"
                value={formData.shipping_date}
                onChange={(e) => setFormData({ ...formData, shipping_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Transportista</Label>
              <Input
                value={formData.carrier}
                onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                placeholder="Ej: DHL, SEUR, etc"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Numero de Tracking</Label>
            <Input
              value={formData.tracking_number}
              onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
              placeholder="Numero de seguimiento"
            />
          </div>

          <div className="space-y-2">
            <Label>Direccion de Destino</Label>
            <Textarea
              value={formData.destination_address}
              onChange={(e) => setFormData({ ...formData, destination_address: e.target.value })}
              placeholder="Direccion completa de entrega"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observaciones adicionales..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : "Crear Envio"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
